// components/create/BasicSection.js
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/BasicSection.scss';

const MAX_DESCRIPTION_LENGTH = 1040;
const MAX_TITLE_LENGTH = 40;

const EVENT_TYPES = [
  'Birthday', 'Wedding', 'Party', 'Conference', 'Workshop', 
  'Meetup', 'Concert', 'Festival', 'Fundraiser', 'Sports Event', 'Other'
];

export default function BasicSection({
  title,
  setTitle,
  eventType,
  setEventType,
  description,
  setDescription,
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  endDate,
  setEndDate,
  endTime,
  setEndTime,
  location,
  onLocationPress,
  allowGuestUploads,
  setAllowGuestUploads,
  allowGuestComments,
  setAllowGuestComments,
  customFields,
  setCustomFields,
  polls,
  setPolls,
  playlist,
  setPlaylist,
  youtubePlaylist,
  setYoutubePlaylist,
  errors = {},
}) {
  const [showEventTypeDropdown, setShowEventTypeDropdown] = useState(false);
  const [showCustomFieldModal, setShowCustomFieldModal] = useState(false);
  const [currentCustomField, setCurrentCustomField] = useState(null);

  const formatDate = (date) => {
    if (!date) return 'Select date';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (time) => {
    if (!time) return 'Select time';
    return time.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleAddCustomField = () => {
    setCurrentCustomField(null);
    setShowCustomFieldModal(true);
  };

  const handleEditCustomField = (field) => {
    setCurrentCustomField(field);
    setShowCustomFieldModal(true);
  };

  const handleSaveCustomField = (field) => {
    const index = customFields.findIndex((f) => f.id === field.id);
    if (index !== -1) {
      const newFields = [...customFields];
      newFields[index] = field;
      setCustomFields(newFields);
    } else {
      setCustomFields([...customFields, field]);
    }
    setShowCustomFieldModal(false);
  };

  const handleDeleteCustomField = (id) => {
    setCustomFields(customFields.filter((field) => field.id !== id));
  };

  return (
    <div className="basic-section">
      {/* Title */}
      <div className="section-card">
        <label className="section-title">Invitation Title *</label>
        <input
          type="text"
          className={`text-input ${errors.title ? 'error' : ''}`}
          placeholder="Enter invitation title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={MAX_TITLE_LENGTH}
        />
        <div className="char-count">
          {title.length}/{MAX_TITLE_LENGTH}
        </div>
        {errors.title && <p className="error-text">{errors.title}</p>}
      </div>

      {/* Event Type */}
      <div className="section-card">
        <label className="section-title">Event Type *</label>
        <div className="dropdown-container">
          <button
            type="button"
            className={`select-button ${errors.eventType ? 'error' : ''}`}
            onClick={() => setShowEventTypeDropdown(!showEventTypeDropdown)}
          >
            <i className="icon-pricetag-outline"></i>
            <span>{eventType || 'Select event type'}</span>
            <i className="icon-chevron-down"></i>
          </button>
          
          {showEventTypeDropdown && (
            <div className="dropdown-menu">
              {EVENT_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  className="dropdown-item"
                  onClick={() => {
                    setEventType(type);
                    setShowEventTypeDropdown(false);
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          )}
        </div>
        {errors.eventType && <p className="error-text">{errors.eventType}</p>}
      </div>

      {/* Date & Time */}
      <div className="section-card">
        <label className="section-title">Date & Time *</label>
        
        <label className="field-label">Start</label>
        <div className="datetime-row">
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            dateFormat="EEE, MMM d, yyyy"
            placeholderText="Select date"
            className={`datetime-input ${errors.startDate ? 'error' : ''}`}
            wrapperClassName="datetime-wrapper"
          />
          <DatePicker
            selected={startTime}
            onChange={(time) => setStartTime(time)}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            placeholderText="Select time"
            className={`datetime-input ${errors.startTime ? 'error' : ''}`}
            wrapperClassName="datetime-wrapper"
          />
        </div>

        <label className="field-label">End (Optional)</label>
        <div className="datetime-row">
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            dateFormat="EEE, MMM d, yyyy"
            placeholderText="Same day"
            className="datetime-input"
            wrapperClassName="datetime-wrapper"
          />
          <DatePicker
            selected={endTime}
            onChange={(time) => setEndTime(time)}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            placeholderText="Select time"
            className="datetime-input"
            wrapperClassName="datetime-wrapper"
          />
        </div>
        
        {errors.startDateTime && <p className="error-text">{errors.startDateTime}</p>}
      </div>

      {/* Location */}
      <div className="section-card">
        <label className="section-title">Location</label>
        <button
          type="button"
          className={`select-button ${errors.location ? 'error' : ''}`}
          onClick={onLocationPress}
        >
          <i className="icon-location-outline"></i>
          <span>{location?.display_name || 'Venue TBD'}</span>
          <i className="icon-search"></i>
        </button>
        
        {location && (
          <div className="selected-location">
            <div className="location-details">
              <p className="location-name">{location.display_name}</p>
              <p className="location-address">{location.formatted_address}</p>
            </div>
          </div>
        )}
        {errors.location && <p className="error-text">{errors.location}</p>}
      </div>

      {/* Description */}
      <div className="section-card">
        <label className="section-title">Details</label>
        <textarea
          className="textarea-input"
          placeholder="Share more details about your event..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={MAX_DESCRIPTION_LENGTH}
          rows={6}
        />
        <div className="char-count">
          {description.length}/{MAX_DESCRIPTION_LENGTH}
        </div>
      </div>

      {/* Photo Pool */}
      <div className="section-card">
        <label className="section-title">Photo Pool</label>
        <div className="toggle-row">
          <div className="toggle-info">
            <p className="toggle-label">Allow Guest Uploads</p>
            <p className="toggle-description">Let guests share photos from the event</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={allowGuestUploads}
              onChange={(e) => setAllowGuestUploads(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
        
        <div className="toggle-row">
          <div className="toggle-info">
            <p className="toggle-label">Allow Comments</p>
            <p className="toggle-description">Enable comments on photos</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={allowGuestComments}
              onChange={(e) => setAllowGuestComments(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Custom Fields */}
      <div className="section-card">
        <label className="section-title">Additional Information</label>
        <p className="section-description">
          Add custom fields like dress code, parking info, or website links
        </p>
        
        {customFields.map((field) => (
          <div key={field.id} className="custom-field-item">
            <div className="field-content">
              <i className={`icon-${field.icon || 'create-outline'}`}></i>
              <div className="field-text">
                <p className="field-label">{field.label}</p>
                <p className="field-value">{field.value}</p>
              </div>
            </div>
            <div className="field-actions">
              <button
                type="button"
                className="icon-button"
                onClick={() => handleEditCustomField(field)}
              >
                <i className="icon-create"></i>
              </button>
              <button
                type="button"
                className="icon-button"
                onClick={() => handleDeleteCustomField(field.id)}
              >
                <i className="icon-trash"></i>
              </button>
            </div>
          </div>
        ))}
        
        <button
          type="button"
          className="add-button"
          onClick={handleAddCustomField}
        >
          <i className="icon-add-circle"></i>
          <span>Add Custom Field</span>
        </button>
      </div>

      {/* Playlists */}
      <div className="section-card">
        <label className="section-title">Music</label>
        <p className="section-description">Share your event playlist</p>
        
        <div className="input-group">
          <label className="field-label">Spotify Playlist URL</label>
          <input
            type="url"
            className="text-input"
            placeholder="https://open.spotify.com/playlist/..."
            value={playlist}
            onChange={(e) => setPlaylist(e.target.value)}
          />
        </div>

        <div className="input-group">
          <label className="field-label">YouTube Playlist URL</label>
          <input
            type="url"
            className="text-input"
            placeholder="https://www.youtube.com/playlist?list=..."
            value={youtubePlaylist}
            onChange={(e) => setYoutubePlaylist(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}